import React, { useContext, useEffect, useState } from "react";
import {
    Container,
    Table,
    Button,
    Row,
    Col,
    Input,
    Label
} from "reactstrap";
import PaginationMaterial from "../../components/PaginationMaterial/PaginationMaterial";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import Header from "../../components/Headers/Header";
import AdminNavbar from "../../components/Navbars/AdminNavbar/AdminNavbar";
import { AuthContext } from "../../context/AuthContext";
import { AlunoContext } from "../../context/AlunoContext";
import { TerceiroContext } from '../../context/TerceiroContext';
import { CursoContext } from '../../context/CursoContext';
import { ContratoContext } from "../../context/ContratoContext";
import { PreparacaoAlbumContext } from "../../context/PreparacaoAlbumContext";
import { RelatorioVendaContext } from "../../context/RelatorioVendaContext";
import { EditAlunoModal } from "../../components/Aluno/EditAlunoModal";
import { EditAlunoType } from "../../context/hooks/useAluno";

const AlunoDetalhado = () => {
    const { objUserTk } = useContext(AuthContext);
    const { getAlunosDetalhadosPaged, listAlunoDetalhado, alunoDetalhadoPageCount, handleGetAlunoById } = useContext(AlunoContext);
    const { handleGetVendedoresByEmpresa, listVendedores } = useContext(TerceiroContext);
    const { handleGetCursoByEmpresaId, listCurso } = useContext(CursoContext);
    const { handleGetContratoByEmpresaId, listContrato } = useContext(ContratoContext);
    const { handleGetAlbumByEmpresaId, listPreparacaoAlbum } = useContext(PreparacaoAlbumContext);
    const { handleGetRelatorioByEmpresaId, listRelatorioVenda } = useContext(RelatorioVendaContext);

    const [loading, setLoading] = useState(true);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [alunoSelecionado, setAlunoSelecionado] = useState<EditAlunoType | null>(null);
    const [contratoIdSelecionado, setContratoIdSelecionado] = useState<number | undefined>(undefined);
    const [cursoIdSelecionado, setCursoIdSelecionado] = useState<number | undefined>(undefined);
    const [tipoFiltro, setTipoFiltro] = useState<string>("codigo");
    const [termoBusca, setTermoBusca] = useState<string>("");
    const [cursoIdFiltro, setCursoIdFiltro] = useState<string>("");
    const [contratoIdFiltro, setContratoIdFiltro] = useState<string>("");
    const [relatorioIdFiltro, setRelatorioIdFiltro] = useState<string>("");
    const [hasBuscado, setHasBuscado] = useState<boolean>(false);

    const executarBusca = (
        termo: string,
        tipo: string,
        curso: string,
        contrato: string,
        relatorio: string,
        page = 1
    ) => {
        if (!objUserTk?.EmpresaId) return;

        let nameFilter: string | undefined = undefined;
        let alunoIdFilter: number | undefined = undefined;
        let cidadeFilter: string | undefined = undefined;

        if (termo.trim()) {
            if (tipo === "codigo") {
                alunoIdFilter = parseInt(termo.trim()) || undefined;
            } else if (tipo === "nome") {
                nameFilter = termo.trim();
            } else if (tipo === "cidade") {
                cidadeFilter = termo.trim();
            }
        }

        setLoading(true);
        setHasBuscado(true);

        getAlunosDetalhadosPaged(
            page,
            objUserTk.EmpresaId,
            10,
            nameFilter,
            alunoIdFilter,
            contrato ? parseInt(contrato) : undefined,
            relatorio ? parseInt(relatorio) : undefined,
            undefined,
            cidadeFilter,
            curso ? parseInt(curso) : undefined
        )
            .then(() => setLoading(false))
            .catch(() => setLoading(false));
    };

    useEffect(() => {
        if (objUserTk?.EmpresaId) {
            setLoading(true);

            Promise.all([
                handleGetCursoByEmpresaId(objUserTk.EmpresaId),
                handleGetVendedoresByEmpresa(objUserTk.EmpresaId),
                handleGetContratoByEmpresaId(objUserTk.EmpresaId),
                handleGetAlbumByEmpresaId(objUserTk.EmpresaId),
                handleGetRelatorioByEmpresaId(objUserTk.EmpresaId),
            ])
                .then(() => {
                    return getAlunosDetalhadosPaged(1, objUserTk.EmpresaId, 10);
                })
                .then(() => {
                    setHasBuscado(true);
                    setLoading(false);
                })
                .catch(() => setLoading(false));
        }
    }, [objUserTk.EmpresaId]);

    // Debounce automático ao digitar no termo de busca ou filtros
    useEffect(() => {
        if (!objUserTk?.EmpresaId) return;

        const timer = setTimeout(() => {
            executarBusca(termoBusca, tipoFiltro, cursoIdFiltro, contratoIdFiltro, relatorioIdFiltro, 1);
        }, 400);

        return () => clearTimeout(timer);
    }, [termoBusca, tipoFiltro, cursoIdFiltro, contratoIdFiltro, relatorioIdFiltro]);

    const onChangePagination = async (e: any, value: number) => {
        executarBusca(termoBusca, tipoFiltro, cursoIdFiltro, contratoIdFiltro, relatorioIdFiltro, value);
    };

    const handleClear = () => {
        setTermoBusca("");
        setCursoIdFiltro("");
        setContratoIdFiltro("");
        setRelatorioIdFiltro("");
        setTipoFiltro("codigo");
        if (objUserTk?.EmpresaId) {
            setLoading(true);
            getAlunosDetalhadosPaged(1, objUserTk.EmpresaId, 10)
                .then(() => setLoading(false))
                .catch(() => setLoading(false));
        }
    };

    const handleEditarAluno = async (alunoId: number, contratoId?: number, cursoId?: number) => {
        const aluno = await handleGetAlunoById(alunoId);
        if (!aluno) return;
        setAlunoSelecionado({
            id: aluno.id,
            referenceCode: aluno.referenceCode,
            name: aluno.name,
            email: aluno.email,
            cpf: aluno.cpf,
            address: aluno.address,
            addressNumber: aluno.addressNumber,
            neighborhood: aluno.neighborhood,
            city: aluno.city,
            state: aluno.state,
            zipCode: aluno.zipCode,
            landline: aluno.landline,
            telephone_01: aluno.telephone_01,
            telephone_02: aluno.telephone_02,
            telephone_03: aluno.telephone_03,
            telephone_04: aluno.telephone_04,
            telephone_05: aluno.telephone_05,
            telephone_06: aluno.telephone_06,
            facebook: aluno.facebook,
            instagram: aluno.instagram,
            keepThePhotos: aluno.keepThePhotos,
            hasAlbum: aluno.hasAlbum,
            numberOfPhotos: aluno.numberOfPhotos,
            base64Image: aluno.base64Image,
            observacao: aluno.observacao,
        });
        setContratoIdSelecionado(contratoId);
        setCursoIdSelecionado(cursoId);
        setEditModalOpen(true);
    };

    const handleAlunoAtualizado = () => {
        executarBusca(termoBusca, tipoFiltro, cursoIdFiltro, contratoIdFiltro, relatorioIdFiltro, 1);
    };

    const getRelacaoStatusLabel = (status: number) => {
        switch (status) {
            case 1:
                return "Em rota";
            case 2:
                return "Roteiro";
            case 3:
                return "Retorno";
            case 4:
                return "Devolvido";
            case 5:
                return "Vendido";
            case 6:
                return "Descartado";
            case 7:
                return "Recusou Álbum";
            default:
                return "Desconhecido";
        }
    };

    const getPlaceholderBusca = () => {
        switch (tipoFiltro) {
            case "codigo":
                return "Digite o código do aluno...";
            case "nome":
                return "Digite o nome do aluno...";
            case "cidade":
                return "Digite a cidade...";
            default:
                return "Pesquisar...";
        }
    };

    return (
        <>
            <LoadingSpinner />
            <Header />
            <AdminNavbar brandText="Álbum dos Alunos" search={false} titleSearch="" />
            <Container fluid className="mt-4 px-4">
                <div className="card shadow-sm border-0 mb-4 p-4">
                    <h2 className="text-primary font-weight-bold mb-1" style={{ color: '#0d2b45' }}>Aluno detalhado</h2>
                    <p className="text-muted mb-4 small">Pesquise um formando para visualizar suas informações.</p>

                    <Row className="align-items-end">
                        <Col xs="12" md="3" lg="2" className="mb-3">
                            <Label className="form-control-label font-weight-bold" for="tipoFiltro">Filtrar por</Label>
                            <Input
                                className="form-control-alternative"
                                type="select"
                                id="tipoFiltro"
                                value={tipoFiltro}
                                onChange={(e) => setTipoFiltro(e.target.value)}
                            >
                                <option value="codigo">Código</option>
                                <option value="nome">Nome</option>
                                <option value="cidade">Cidade</option>
                            </Input>
                        </Col>

                        <Col xs="12" md="5" lg="4" className="mb-3">
                            <Label className="form-control-label font-weight-bold" for="termoBusca">Pesquisa</Label>
                            <Input
                                className="form-control-alternative"
                                type={tipoFiltro === "codigo" ? "number" : "text"}
                                id="termoBusca"
                                placeholder={getPlaceholderBusca()}
                                value={termoBusca}
                                onChange={(e) => setTermoBusca(e.target.value)}
                            />
                        </Col>

                        <Col xs="12" md="4" lg="2" className="mb-3">
                            <Label className="form-control-label font-weight-bold" for="cursoIdFiltro">Curso</Label>
                            <Input
                                className="form-control-alternative"
                                type="select"
                                id="cursoIdFiltro"
                                value={cursoIdFiltro}
                                onChange={(e) => setCursoIdFiltro(e.target.value)}
                            >
                                <option value="">Todos os cursos</option>
                                {listCurso.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </Input>
                        </Col>

                        <Col xs="12" md="4" lg="2" className="mb-3">
                            <Label className="form-control-label font-weight-bold" for="contratoIdFiltro">Contrato</Label>
                            <Input
                                className="form-control-alternative"
                                type="select"
                                id="contratoIdFiltro"
                                value={contratoIdFiltro}
                                onChange={(e) => setContratoIdFiltro(e.target.value)}
                            >
                                <option value="">Todos os contratos</option>
                                {listContrato.map((ct) => (
                                    <option key={ct.id} value={ct.id}>{ct.id}</option>
                                ))}
                            </Input>
                        </Col>

                        <Col xs="12" md="4" lg="2" className="mb-3">
                            <Label className="form-control-label font-weight-bold" for="relatorioIdFiltro">Relatório</Label>
                            <Input
                                className="form-control-alternative"
                                type="select"
                                id="relatorioIdFiltro"
                                value={relatorioIdFiltro}
                                onChange={(e) => setRelatorioIdFiltro(e.target.value)}
                            >
                                <option value="">Todos os relatórios</option>
                                {listRelatorioVenda.map((r) => (
                                    <option key={r.id} value={r.id}>{r.id}</option>
                                ))}
                            </Input>
                        </Col>

                        <Col xs="12" className="d-flex justify-content-end mb-2">
                            <Button
                                style={{ color: "#FFF", backgroundColor: "#32325d" }}
                                onClick={() => executarBusca(termoBusca, tipoFiltro, cursoIdFiltro, contratoIdFiltro, relatorioIdFiltro, 1)}
                                className="mr-2"
                            >
                                <i className="fa-solid fa-magnifying-glass mr-1"></i> Buscar
                            </Button>
                            <Button
                                style={{ color: "#FFF", backgroundColor: "#f5365c" }}
                                onClick={handleClear}
                            >
                                <i className="fa-solid fa-trash-can mr-1"></i> Limpar
                            </Button>
                        </Col>
                    </Row>
                </div>
                <Table hover responsive className="table-striped">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Código ref.</th>
                            <th>Foto</th>
                            <th>Nome</th>
                            <th>Email</th>
                            <th>Curso</th>
                            <th>Contrato ID</th>
                            <th>Relatório ID</th>
                            <th>Vendedor ID</th>
                            <th>Vendedor</th>
                            <th>Instituição</th>
                            <th>Cidade</th>
                            <th>Fotos</th>
                            <th>Status</th>
                            <th>Justificativa</th>
                            <th>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {listAlunoDetalhado.map((aluno, index) => (
                            <tr key={index}>
                                <td>{aluno.alunoId}</td>
                                <td>{aluno.referenceCode}</td>
                                <td style={{ textAlign: "center" }}>
                                    {aluno.base64Image ? (
                                        <img
                                            src={aluno.base64Image}
                                            alt={`Foto de ${aluno.alunoNome}`}
                                            style={{
                                                width: "40px",
                                                height: "40px",
                                                borderRadius: "50%",
                                                objectFit: "cover",
                                                boxShadow: "0 0 3px rgba(0,0,0,0.3)"
                                            }}
                                            title={aluno.alunoNome}
                                        />
                                    ) : (
                                        <div
                                            title={aluno.alunoNome}
                                            style={{
                                                width: "40px",
                                                height: "40px",
                                                borderRadius: "50%",
                                                backgroundColor: "#ccc"
                                            }}
                                        />
                                    )}
                                </td>
                                <td>{aluno.alunoNome}</td>
                                <td>{aluno.email}</td>
                                <td>{aluno.cursoNome}</td>
                                <td>{aluno.contratoId}</td>
                                <td>{aluno.relatorioId ?? "-"}</td>
                                <td>{aluno.vendedorId ?? "-"}</td>
                                <td>{aluno.vendedorNome ?? "-"}</td>
                                <td>{aluno.instituicaoNome ?? "-"}</td>
                                <td>{aluno.cidade ?? "-"}</td>
                                <td>
                                    {Number(aluno.status) === 5 ? (
                                        <div>
                                            <span className="badge badge-success">Vendido</span>
                                            {aluno.vendaValor != null && (
                                                <div className="small font-weight-bold text-success mt-1">
                                                    {aluno.vendaValor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        getRelacaoStatusLabel(Number(aluno.status)) ?? "-"
                                    )}
                                </td>
                                <td title={aluno.status === 7 ? aluno.justificativaRecusa : undefined}>
                                    {aluno.status === 7
                                        ? (aluno.justificativaRecusa && aluno.justificativaRecusa.length > 40
                                            ? aluno.justificativaRecusa.substring(0, 40) + "…"
                                            : aluno.justificativaRecusa ?? "—")
                                        : "—"}
                                </td>
                                <td>
                                    <Button onClick={() => handleEditarAluno(aluno.alunoId, aluno.contratoId, aluno.cursoId)}><i className="fa-solid fa-pen-to-square"></i></Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>

                <div className="d-flex justify-content-end mt-3">
                    {alunoDetalhadoPageCount > 1 && (
                        <PaginationMaterial
                            onChange={onChangePagination}
                            count={alunoDetalhadoPageCount}
                        />
                    )}
                </div>
            </Container>
            <EditAlunoModal
                isOpen={editModalOpen}
                toggle={() => setEditModalOpen(false)}
                aluno={alunoSelecionado}
                contratoId={contratoIdSelecionado}
                cursoId={cursoIdSelecionado}
                onAlunoAtualizado={handleAlunoAtualizado}
            />
        </>
    );
};

export default AlunoDetalhado;
